import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowTopRightOnSquareIcon, ClockIcon, DocumentMagnifyingGlassIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { openProSearch } from '../../utils/proSearch';
import systemEventsService, {
  type RunResults,
  type SystemEventRun,
  type SystemEventSummary,
  type SystemOption,
} from '../../services/systemEvents.service';

/**
 * System Events — 360 port of v1 eventhistory.pl.
 *
 * Lists every event type that has run, newest first, and drills into one type's
 * full history. V1 has one of these screens per application, each hard-wired to
 * its own schema; here the system is a dropdown, so CMS (every upload) and
 * Quotes (the Salesforce import/export) share one page.
 *
 * "View results" appears only on runs made in THIS application, which stored
 * their own results. V1's equivalent link points at an HTML file on the Perl
 * box, so a legacy run stays viewable in the legacy system.
 */
const EventHistoryPage: React.FC = () => {
  const [systems, setSystems] = useState<SystemOption[]>([]);
  const [system, setSystem] = useState<string>('cms');
  const [events, setEvents] = useState<SystemEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<string | null>(null);
  const [history, setHistory] = useState<SystemEventRun[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [results, setResults] = useState<RunResults | null>(null);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [drawerPro, setDrawerPro] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(null);
    setHistory([]);
    systemEventsService.list(system)
      .then((d) => {
        if (cancelled) return;
        setEvents(d.events);
        if (d.systems?.length) setSystems(d.systems);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [system]);

  const openType = useCallback((type: string) => {
    setSelected(type);
    setHistoryLoading(true);
    setHistory([]);
    systemEventsService.history(type, system)
      .then((d) => setHistory(d.history))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [system]);

  const openResults = useCallback(async (type: string, batchId: string) => {
    setResultsError(null);
    setResults(null);
    try {
      setResults(await systemEventsService.results(type, batchId));
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setResultsError(detail || 'Could not load the results for this run.');
    }
  }, []);

  return (
    <div className="p-[18px] max-w-[1600px] mx-auto">
      <div className="flex items-center gap-1.5 mb-3">
        <ClockIcon className="h-5 w-5 text-accent-text" />
        <h1 className="m-0 text-[0.9375rem] font-bold text-heading">System Events</h1>
        <label className="ml-auto flex items-center gap-1.5 text-[0.75rem] text-muted">
          System
          <select
            value={system}
            onChange={(e) => setSystem(e.target.value)}
            aria-label="System"
            className="text-[0.75rem] border border-rule rounded px-1.5 py-1 bg-input text-body"
          >
            {(systems.length ? systems : [{ key: 'cms', label: 'CMS' }]).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      <section className="bg-panel border border-rule rounded-lg overflow-hidden">
        {loading && <Empty>Loading…</Empty>}
        {!loading && error && (
          <div className="px-4 py-6 text-center text-danger-fg text-[0.78125rem]">
            Failed to load: {error}
          </div>
        )}
        {!loading && !error && events.length === 0 && (
          <Empty>No system events recorded yet.</Empty>
        )}
        {!loading && !error && events.length > 0 && (
          <table className="w-full border-collapse text-[0.75rem]">
            <thead>
              <tr>
                {['Event Type', 'Last Event Date', 'User', 'Last Result', 'Runs', '', ''].map((h, i) => (
                  <Th key={`${h}-${i}`} className={h === 'Last Result' ? 'w-full' : 'whitespace-nowrap'}>
                    {h}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr
                  key={e.type}
                  className={`hover:bg-highlight ${selected === e.type ? 'bg-sunken' : ''}`}
                >
                  <Td className="font-medium text-body">{e.type}</Td>
                  <Td className="text-muted whitespace-nowrap">{e.lastDate || '—'}</Td>
                  <Td className="text-muted">{e.user || '—'}</Td>
                  <Td className="text-muted" title={e.lastResult}>
                    <span className="block truncate">{e.lastResult || '—'}</span>
                  </Td>
                  <Td className="text-muted text-right tabular-nums">{e.count}</Td>
                  <Td className="whitespace-nowrap">
                    {e.hasDetails ? (
                      <button
                        type="button"
                        onClick={() => openResults(e.type, e.batchId)}
                        className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-accent-text hover:underline"
                      >
                        <DocumentMagnifyingGlassIcon className="h-3.5 w-3.5" />
                        Last Results
                      </button>
                    ) : (
                      <span className="text-[0.6875rem] text-muted" title="The latest run was made in the legacy system — its results live there">
                        legacy run
                      </span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openType(e.type)}
                      className="text-[0.75rem] font-medium text-accent-text hover:underline"
                    >
                      History
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {selected && (
        <section className="mt-3 bg-panel border border-rule rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-sunken border-b border-rule">
            <span className="text-[0.78125rem] font-bold text-heading">{selected}</span>
            <span className="text-[0.75rem] text-muted">— every run, newest first</span>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="ml-auto text-[0.75rem] font-medium text-accent-text hover:underline"
            >
              Close
            </button>
          </div>
          {historyLoading && <Empty>Loading…</Empty>}
          {!historyLoading && history.length === 0 && <Empty>No history.</Empty>}
          {!historyLoading && history.length > 0 && (
            <table className="w-full border-collapse text-[0.75rem]">
              <thead>
                <tr>
                  {['Date', 'User', 'File', 'Result', ''].map((h) => (
                    <Th key={h} className={h === 'Result' ? 'w-full' : 'whitespace-nowrap'}>
                      {h}
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((r, i) => (
                  <tr key={`${r.batchId}-${i}`} className="hover:bg-highlight">
                    <Td className="text-muted whitespace-nowrap">{r.datetime || '—'}</Td>
                    <Td className="text-muted">{r.user || '—'}</Td>
                    <Td className="text-muted">{r.file || '—'}</Td>
                    <Td className="text-body">{r.result || '—'}</Td>
                    <Td className="whitespace-nowrap">
                      {r.hasDetails ? (
                        <button
                          type="button"
                          onClick={() => openResults(selected, r.batchId)}
                          className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-accent-text hover:underline"
                        >
                          <DocumentMagnifyingGlassIcon className="h-3.5 w-3.5" />
                          View results
                        </button>
                      ) : (
                        <span className="text-[0.6875rem] text-muted" title="Run in the legacy system — its results live there">
                          legacy run
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {resultsError && (
        <div className="mt-3 px-3 py-2 text-[0.75rem] text-danger-fg bg-danger border border-rule rounded">
          {resultsError}
        </div>
      )}
      {results && (
        <ResultsModal
          results={results}
          onClose={() => setResults(null)}
          // The drawer is z-100 and this modal z-130, so they cannot usefully
          // stack. Clicking a PRO means "go look at this one" — close the
          // results and open it; Last Results is one click away afterwards.
          onOpenPro={(pro) => { setResults(null); setDrawerPro(pro); }}
        />
      )}

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={() => setDrawerPro(null)}
      />
    </div>
  );
};

/**
 * The stored results of one run made in this application.
 *
 * A modal rather than another section down the page: it is opened from two
 * places — the list's Last Results and a row of the drill — and appending it to
 * the bottom meant scrolling past a history that can run to 500 rows to read
 * the thing you just asked for.
 */
const ResultsModal: React.FC<{
  results: RunResults;
  onClose: () => void;
  onOpenPro: (pro: number) => void;
}> = ({ results, onClose, onOpenPro }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const p = results.payload || {};
  const rows = p.created ?? [];
  // Deduped: one run can put more than one accessorial on the same order.
  const pros = [...new Set(
    rows.map((r) => Number(r.pronumber)).filter((n) => Number.isFinite(n) && n > 0),
  )];

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-scrim/40 flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Results of the ${results.eventType} run`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-panel border border-rule rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-rule">
          <div className="min-w-0">
            <div className="text-[0.8125rem] font-bold text-heading">Run results</div>
            <div className="text-[0.75rem] text-muted truncate">
              {results.eventType} · {results.file} · {results.createdAt} · {results.user}
            </div>
          </div>
          {pros.length > 0 && (
            <button
              type="button"
              onClick={() => openProSearch(pros)}
              className="ml-auto inline-flex items-center gap-1 text-[0.75rem] font-medium text-accent-text hover:underline whitespace-nowrap"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Open all {pros.length} {pros.length === 1 ? 'PRO' : 'PROs'} in search results
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close results"
            className={`${pros.length > 0 ? '' : 'ml-auto '}text-muted hover:text-body`}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-4 px-4 py-2 text-[0.75rem] text-body border-b border-hairline">
          {p.invoiceCount != null && <span><b>{p.invoiceCount}</b> invoices</span>}
          {p.lineCount != null && <span><b>{p.lineCount}</b> line items</span>}
          {p.totalRows != null && <span>from <b>{p.totalRows}</b> rows</span>}
          {!!p.skippedCount && <span className="text-muted"><b>{p.skippedCount}</b> skipped</span>}
          {p.grandTotal != null && <span className="ml-auto">Total <b>{p.grandTotal}</b></span>}
        </div>

        {rows.length > 0 && (
          <div className="overflow-auto">
            <table className="w-full border-collapse text-[0.6875rem]">
              <thead className="sticky top-0 bg-sunken">
                <tr>
                  {['PRO', 'MAS Tag', 'Accessorial #', 'Lines', 'Total'].map((h) => (
                    <Th key={h} className="whitespace-nowrap">{h}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="hover:bg-highlight">
                    <Td>
                      {Number(r.pronumber) > 0 ? (
                        <button
                          type="button"
                          onClick={() => onOpenPro(Number(r.pronumber))}
                          className="font-medium text-accent-text hover:underline"
                          title="Open this order for review"
                        >
                          {String(r.pronumber)}
                        </button>
                      ) : (
                        <span className="font-medium text-body">—</span>
                      )}
                    </Td>
                    <Td className="text-muted">{String(r.mas_tag ?? '—')}</Td>
                    <Td className="text-muted">{String(r.accessorial_num ?? '—')}</Td>
                    <Td className="text-muted text-right tabular-nums">{String(r.line_count ?? '—')}</Td>
                    <Td className="text-body text-right tabular-nums">{String(r.total ?? '—')}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <th className={`px-3 py-2 text-left text-[0.65625rem] font-semibold text-muted uppercase tracking-wider bg-sunken border-b border-rule ${className}`}>
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode; className?: string; title?: string }> = ({
  children, className = '', title,
}) => (
  <td title={title} className={`px-3 py-1.5 border-b border-hairline ${className}`}>{children}</td>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-4 py-10 text-center text-muted text-[0.8125rem]">{children}</div>
);

export default EventHistoryPage;
